import React from 'react';
import { Box, Container, Typography, List, ListItem, ListItemContent, Link, Divider } from '@mui/joy';
import { animate, stagger } from 'motion';
import { useEffect, useRef } from 'react';

const UseRestrictions = () => {
    const containerRef = useRef(null);

    useEffect(() => {
        if (!containerRef.current) return;

        const elements = containerRef.current.querySelectorAll('.animate-in');
        
        animate(
            elements,
            { 
                opacity: [0, 1],
                y: [20, 0]
            },
            { 
                delay: stagger(0.1),
                duration: 0.6,
                easing: 'ease-out'
            }
        );
    }, []);

    return (
        <Box ref={containerRef}>
            <Container sx={{ py: 8 }}>
                <Box className="animate-in" sx={{ textAlign: 'center', mb: 8 }}>
                    <Typography
                        level="h1"
                        fontSize="xl4"
                        sx={{
                            mb: 3,
                            bgcolor: 'text.primary',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                        }}
                    >
                        Use Restrictions Policy
                    </Typography>
                    <Typography 
                        level="body1" 
                        fontSize="lg" 
                        sx={{ 
                            maxWidth: 800, 
                            mx: 'auto',
                            color: 'text.secondary',
                            lineHeight: 1.6
                        }}
                    >
                        At Hubitable, our use restrictions ensure a safe, fair environment for all users, compliant with UK laws like the <strong>Consumer Rights Act 2015</strong> and, where relevant, the <strong>Online Safety Act 2023</strong>.
                    </Typography>
                </Box>

                <Box className="animate-in" sx={{ maxWidth: 800, mx: 'auto' }}>
                    <Typography level="h2" sx={{ mb: 3, color: 'text.primary' }}>
                        1. Prohibited Activities
                    </Typography>
                    <Typography level="body1" sx={{ mb: 3, color: 'text.secondary' }}>
                        For the purposes of these Terms, `abuse` refers to any actions that violate the prohibited activities listed below, including but not limited to spamming, hacking, or distributing harmful content.
                    </Typography>
                    <Typography level="body1" sx={{ mb: 3, color: 'text.secondary' }}>
                        You may not use our services for:
                    </Typography>
                    <List size="lg" sx={{ mb: 4 }}>
                        <ListItem>
                            <ListItemContent>
                                <Typography level="body1">
                                    <strong>Illegal Activities</strong>: Anything violating UK law (e.g., fraud, copyright infringement).
                                </Typography>
                            </ListItemContent>
                        </ListItem>
                        <ListItem>
                            <ListItemContent>
                                <Typography level="body1">
                                    <strong>Harmful Content</strong>: Sharing defamatory, obscene, or harassing material.
                                </Typography>
                            </ListItemContent>
                        </ListItem>
                        <ListItem>
                            <ListItemContent>
                                <Typography level="body1">
                                    <strong>Spam and Phishing</strong>: Sending unsolicited messages or collecting data without consent.
                                </Typography>
                            </ListItemContent>
                        </ListItem>
                        <ListItem>
                            <ListItemContent>
                                <Typography level="body1">
                                    <strong>Malware and Hacking</strong>: Distributing viruses or attempting unauthorized access.
                                </Typography>
                            </ListItemContent>
                        </ListItem>
                        <ListItem>
                            <ListItemContent>
                                <Typography level="body1">
                                    <strong>Excessive Usage</strong>: Disrupting service performance (e.g., excessive API calls).
                                </Typography>
                            </ListItemContent>
                        </ListItem>
                        <ListItem>
                            <ListItemContent>
                                <Typography level="body1">
                                    <strong>Automated Bots</strong>: Operating accounts via bots unless permitted.
                                </Typography>
                            </ListItemContent>
                        </ListItem>
                        <ListItem>
                            <ListItemContent>
                                <Typography level="body1">
                                    <strong>Reselling</strong>: Redistributing services without written permission.
                                </Typography>
                            </ListItemContent>
                        </ListItem>
                    </List>

                    <Divider sx={{ my: 4 }} />

                    <Typography level="h2" sx={{ mb: 3, color: 'text.primary' }}>
                        2. User-Generated Content
                    </Typography>
                    <List size="lg" sx={{ mb: 4 }}>
                        <ListItem>
                            <ListItemContent>
                                <Typography level="body1">
                                    You're responsible for your content, which must comply with all applicable UK laws, including the <strong>Copyright, Designs and Patents Act 1988</strong>, <strong>Defamation Act 2013</strong>, and other relevant regulations.
                                </Typography>
                            </ListItemContent>
                        </ListItem>
                        <ListItem>
                            <ListItemContent>
                                <Typography level="body1">
                                    We may remove unlawful content if our automated systems detect it, but do not proactively monitor it.
                                </Typography>
                            </ListItemContent>
                        </ListItem>
                    </List>

                    <Divider sx={{ my: 4 }} />

                    <Typography level="h2" sx={{ mb: 3, color: 'text.primary' }}>
                        3. Enforcement
                    </Typography>
                    <List size="lg" sx={{ mb: 4 }}>
                        <ListItem>
                            <ListItemContent>
                                <Typography level="body1">
                                    Violations may lead to:
                                </Typography>
                                <List size="sm" sx={{ mt: 2 }}>
                                    <ListItem>
                                        <ListItemContent>
                                            <Typography level="body2">
                                                A warning with a chance to correct the issue.
                                            </Typography>
                                        </ListItemContent>
                                    </ListItem>
                                    <ListItem>
                                        <ListItemContent>
                                            <Typography level="body2">
                                                Temporary suspension until resolved.
                                            </Typography>
                                        </ListItemContent>
                                    </ListItem>
                                    <ListItem>
                                        <ListItemContent>
                                            <Typography level="body2">
                                                Termination for severe/repeated breaches - <em>Severe breaches include actions like distributing malware or posing immediate harm to users or the Services. Repeated breaches refer to multiple violations of these restrictions after a warning.</em>
                                            </Typography>
                                        </ListItemContent>
                                    </ListItem>
                                </List>
                            </ListItemContent>
                        </ListItem>
                        <ListItem>
                            <ListItemContent>
                                <Typography level="body1">
                                    We'll notify you beforehand, except in urgent cases requiring immediate action.
                                </Typography>
                            </ListItemContent>
                        </ListItem>
                    </List>

                    <Divider sx={{ my: 4 }} />

                    <Typography level="h2" sx={{ mb: 3, color: 'text.primary' }}>
                        4. Reporting Violations
                    </Typography>
                    <Typography level="body1" sx={{ mb: 4, color: 'text.secondary' }}>
                        Report issues to{' '}
                        <Link href="mailto:info@hubitable.co.uk" sx={{ color: 'primary.500' }}>
                            Hubitable Support
                        </Link> for investigation.
                    </Typography>
                </Box>
            </Container>
        </Box>
    );
};

export default UseRestrictions; 